import { map } from "lodash";
import { Box, Select } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useCallback, useState } from "react";

import SelectComponents from "src/components/SelectComponents";
import { GroupTypesPropType } from "src/custom-prop-types/groupTypes";
import getInputLabel from "src/util/getInputLabel";

import FormatOptionLabel from "./FormatOptionLabel";

const noOptions = () => "No search results";

const TeamDropdown = ({
  id,
  label,
  height,
  placeholder,
  isSearchable,
  isLoading,
  options,
  loadOptions,
  onSelectTeam,
  value,
  noOptionsMessage = noOptions,
  isDisabled,
  isClearable,
  groupTypes,
  icon,
  formatOptionLabel,
  invalid,
  mandatory,
  focus,
  cacheOptions = true,
  menuPortalTarget: menuPortalTargetProp,
  menuPosition: menuPositionProp,
  updateStyles: updateStylesProp,
}) => {
  const [dialogPortalTarget, setDialogPortalTarget] = useState(null);
  const attachDialogPortal = useCallback((node) => {
    if (!node) {
      setDialogPortalTarget(null);
      return;
    }
    setDialogPortalTarget(
      node.closest("#modal") || node.closest('[role="dialog"]')
    );
  }, []);

  const menuPortalTarget = menuPortalTargetProp || dialogPortalTarget;
  const menuPosition =
    menuPositionProp || (menuPortalTarget ? "fixed" : undefined);
  const updateStyles = (styles) => {
    const withPortal = menuPortalTarget
      ? {
          ...styles,
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }
      : styles;
    return updateStylesProp ? updateStylesProp(withPortal) : withPortal;
  };

  const loadOptionsCallback = (term, callback) => {
    loadOptions(term, (opts) => {
      const newOpts = map(opts, (opt) => {
        return {
          ...opt,
          groupTypes,
        };
      });
      callback(newOpts);
    });
  };

  const formatOptionLabelFn =
    formatOptionLabel ||
    ((data, event) => {
      return FormatOptionLabel(
        {
          ...data,
          groupTypes: data.groupTypes ?? groupTypes,
          icon,
        },
        event
      );
    });

  return (
    <Box ref={attachDialogPortal} width="100%">
      <Select
        inputId={id}
        label={getInputLabel(label, invalid)}
        ariaLabel={placeholder || "Search and select team"}
        placeholder={placeholder}
        noOptionsMessage={noOptionsMessage}
        cacheOptions={cacheOptions}
        backspaceRemovesValue={true}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isLoading={isLoading}
        defaultOptions={options}
        loadOptions={loadOptionsCallback}
        onChange={onSelectTeam}
        formatOptionLabel={formatOptionLabelFn}
        value={value}
        menuPlacement="auto"
        isDisabled={isDisabled}
        components={SelectComponents}
        selectType="async"
        height={height}
        invalid={invalid}
        mandatory={mandatory}
        focus={focus}
        menuPortalTarget={menuPortalTarget || undefined}
        menuPosition={menuPosition}
        menuShouldScrollIntoView={!menuPortalTarget}
        closeMenuOnScroll={Boolean(menuPortalTarget)}
        updateStyles={updateStyles}
      />
    </Box>
  );
};

TeamDropdown.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  icon: PropTypes.string,
  formatOptionLabel: PropTypes.func,
  height: PropTypes.string,
  placeholder: PropTypes.string,
  noOptionsMessage: PropTypes.func,
  isSearchable: PropTypes.bool,
  isLoading: PropTypes.bool,
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  loadOptions: PropTypes.func,
  onSelectTeam: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  isDisabled: PropTypes.bool,
  isClearable: PropTypes.bool,
  groupTypes: GroupTypesPropType,
  invalid: PropTypes.bool,
  mandatory: PropTypes.bool,
  focus: PropTypes.bool,
  cacheOptions: PropTypes.bool,
  menuPortalTarget: PropTypes.object,
  menuPosition: PropTypes.string,
  updateStyles: PropTypes.func,
};

export default TeamDropdown;
